import AdsClickIcon from "@mui/icons-material/AdsClick";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const LOCAL_STORAGE_KEY = "fastveri_waitlist_info_07072026b";
const TARGET_DISPLAY_DATES = new Set(["2026-07-13", "2026-07-16"]);

import { useSnackbar } from "notistack";
import background from "../assets/fastveri/bg.jpg";
import logo from "../assets/fastveri/logo.png";
import screenshot from "../assets/fastveri/screen.png";
import { useAuth } from "../features/auth/AuthContext";
import { useSignUpForNewsletterMutation } from "../features/user/userApiSlice";

const SUCCESS_MESSAGE_PL = "Zostałeś zapisany na listę oczekujących FastVeri!";
const SUCCESS_MESSAGE_EN = "You have been signed up for the FastVeri waitlist!";
const ERROR_MESSAGE_PL =
	"Nie udało się zapisać na listę oczekujących FastVeri.";
const ERROR_MESSAGE_EN = "Failed to sign up for the FastVeri waitlist.";

const getLocalDateKey = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

export const FastVeriWaitlist = () => {
	const { user } = useAuth();
	const { enqueueSnackbar } = useSnackbar();
	const { i18n } = useTranslation();
	const currentLang = i18n?.language || "pl";
	const [isOpen, setOpen] = useState(false);

	const eventType = "fastveri_waitlist";
	const userEmail = user?.email || "";
	const userPhone = user?.phone_number || "";
	const userIdentifier = (userEmail || userPhone).trim().toLowerCase();
	const [signUpForNewsletter, { isLoading: isSigningUp }] =
		useSignUpForNewsletterMutation();

	// lock close button for 10 seconds
	const [lockCloseButton, setLockCloseButton] = useState(true);
	const [lockCounter, setLockCounter] = useState(10);

	// unlock button after 5 seconds
	useEffect(() => {
		if (lockCounter === 0) {
			setLockCloseButton(false);
			return;
		}

		const interval = setInterval(() => {
			setLockCounter((prev) => prev - 1);
		}, 1000);

		return () => clearInterval(interval);
	}, [lockCounter]);

	// show popup only if wasn't shown before
	useEffect(() => {
		if (!userIdentifier) return;

		const today = getLocalDateKey();
		if (!TARGET_DISPLAY_DATES.has(today)) return;

		const dailyUserStorageKey = `${LOCAL_STORAGE_KEY}_${today}_${userIdentifier}`;
		if (localStorage.getItem(dailyUserStorageKey) === "true") return;

		setOpen(true);
		localStorage.setItem(dailyUserStorageKey, "true");
	}, [userIdentifier]);

	const handleSignUpForWaitlist = async () => {
		try {
			await signUpForNewsletter({
				email: userEmail,
				phone_number: userPhone,
				eventType,
			}).unwrap();
			enqueueSnackbar(
				currentLang === "pl" ? SUCCESS_MESSAGE_PL : SUCCESS_MESSAGE_EN,
				{
					variant: "success",
				},
			);
			setOpen(false);
		} catch (error) {
			console.error("Failed to sign up for FastVeri waitlist", error);
			enqueueSnackbar(
				currentLang === "pl" ? ERROR_MESSAGE_PL : ERROR_MESSAGE_EN,
				{
					variant: "error",
				},
			);
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="lg"
			onClose={(_e, reason) => {
				if (reason === "backdropClick") return;
				setOpen(false);
			}}
			open={isOpen}
			disableEscapeKeyDown={true}
		>
			<Box
				sx={{
					padding: "10px 20px",
					background: `url(${background}) no-repeat center/cover`,
					position: "relative",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						position: "absolute",
						top: "10px",
						right: "20px",
						padding: "3px 2px",
						overflow: "hidden",
					}}
				>
					<img src={logo} alt="CARGOseller Logo" width={120} />
				</Box>
				{currentLang === "pl" ? (
					<PLContent
						lockCloseButton={lockCloseButton}
						lockCounter={lockCounter}
						setOpen={setOpen}
						onSignUp={handleSignUpForWaitlist}
						isSigningUp={isSigningUp}
					/>
				) : (
					<ENContent
						lockCloseButton={lockCloseButton}
						lockCounter={lockCounter}
						setOpen={setOpen}
						onSignUp={handleSignUpForWaitlist}
						isSigningUp={isSigningUp}
					/>
				)}
			</Box>
		</Dialog>
	);
};

const PLContent = ({
	lockCloseButton,
	lockCounter,
	setOpen,
	onSignUp,
	isSigningUp,
}) => {
	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
				}}
			>
				<Box
					sx={{
						background: "#33ad67",
						padding: "10px 20px",
					}}
				>
					<Typography sx={{ fontSize: "26px", fontWeight: "600" }}>
						NOWOŚĆ!
					</Typography>
				</Box>
				<Box
					sx={{
						position: "relative",
						background: "#000102",
						padding: "10px 20px",
					}}
				>
					<Typography sx={{ fontSize: "24px", color: "#fff" }}>
						Weryfikujesz przewoźników ręcznie w wielu źródłach?
					</Typography>
					<Typography sx={{ fontSize: "24px", color: "#0084c7" }}>
						Zrób to automatycznie w 1 minutę z{" "}
						<span style={{ color: "#fff" }}>Fast</span>Veri!
					</Typography>
				</Box>
			</Box>
			<Box
				sx={{ display: "flex", justifyContent: "end", position: "relative" }}
			>
				<img
					style={{
						position: "relative",
						bottom: "30px",
						right: "40px",
						transform: "scale(1.06)",
					}}
					src={screenshot}
					alt="FastVeri Screenshot"
					width={640}
				/>
			</Box>
			<Box
				sx={{
					background: "#000102",
					padding: "10px 20px",
				}}
			>
				<Box>
					<Typography sx={{ fontSize: "22px", color: "#fff" }}>
						Dodaj{" "}
						<span style={{ color: "#0084c7", fontWeight: 600 }}>
							4 parametry
						</span>{" "}
						- NIP przewoźnika, email osoby kontaktowej, OCP, wklej parametry
						techniczne maila.
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: "10px",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography sx={{ fontSize: "22px", color: "#fff" }}>
							Gotowy raport{" "}
							<span style={{ color: "#0084c7", fontWeight: 600 }}>
								z ponad 60 parametrami
							</span>{" "}
							z różnych obszarów i źródeł otrzymasz w 1 minutę.
						</Typography>
						<Box sx={{ position: "relative" }}>
							<Typography
								sx={{
									fontSize: "19px",
									color: "#fff",
									"& > a:hover": { textDecoration: "underline !important" },
								}}
							>
								Więcej na{" "}
								<a
									style={{
										color: "#0084c7",
										fontWeight: 600,
										textDecoration: "none",
									}}
									href="https://www.FASTVERI.pl"
									target="_blank"
									rel="noopener noreferrer"
								>
									www.<span style={{ color: "#fff" }}>FAST</span>VERI.pl
									<AdsClickIcon
										sx={{
											position: "absolute",
											top: "14px",
											right: "-20px",
											color: "#fff",
											animation: "bounce 2s infinite",
										}}
									/>
								</a>
							</Typography>
						</Box>
					</Box>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						marginTop: "10px",
					}}
				>
					<Typography sx={{ fontSize: "22px", color: "#fff" }}>
						Czy chcesz weryfikować szybciej, wygodniej oraz skuteczniej chronić
						się przed oszustami?
					</Typography>
				</Box>
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						gap: "20px",
						marginTop: "20px",
						paddingBottom: "16px",
					}}
				>
					<Button
						variant="contained"
						onClick={onSignUp}
						disabled={isSigningUp}
						sx={{
							fontSize: "16px",
							"&:disabled": {
								background: "rgba(99, 194, 154, 0.3)",
								color: "#fff",
							},
						}}
					>
						JESTEM ZAINTERESOWANY
					</Button>
					<Button
						variant="contained"
						color="error"
						disabled={lockCloseButton}
						onClick={() => setOpen(false)}
						sx={{
							fontSize: "16px",
							textTransform: "uppercase",
							background: "#585858",
							"&:hover": {
								background: "#474646",
							},
							"&:disabled": {
								background: "rgba(88, 88, 88, 0.3)",
								color: "#fff",
								cursor: "not-allowed",
							},
						}}
					>
						NIE JESTEM ZAINTERESOWANY {lockCounter > 0 && `(${lockCounter})`}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

const ENContent = ({
	lockCloseButton,
	lockCounter,
	setOpen,
	onSignUp,
	isSigningUp,
}) => {
	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
				}}
			>
				<Box
					sx={{
						background: "#33ad67",
						padding: "10px 20px",
					}}
				>
					<Typography sx={{ fontSize: "26px", fontWeight: "600" }}>
						NEW!
					</Typography>
				</Box>
				<Box
					sx={{
						position: "relative",
						background: "#000102",
						padding: "10px 20px",
					}}
				>
					<Typography sx={{ fontSize: "24px", color: "#fff" }}>
						Do you verify carriers manually across multiple sources?
					</Typography>
					<Typography sx={{ fontSize: "24px", color: "#0084c7" }}>
						Automate it in 1 minute with{" "}
						<span style={{ color: "#fff" }}>Fast</span>Veri!
					</Typography>
				</Box>
			</Box>
			<Box
				sx={{ display: "flex", justifyContent: "end", position: "relative" }}
			>
				<img
					style={{
						position: "relative",
						bottom: "30px",
						right: "40px",
						transform: "scale(1.06)",
					}}
					src={screenshot}
					alt="FastVeri Screenshot"
					width={640}
				/>
			</Box>
			<Box
				sx={{
					background: "#000102",
					padding: "10px 20px",
				}}
			>
				<Box>
					<Typography sx={{ fontSize: "22px", color: "#fff" }}>
						Add{" "}
						<span style={{ color: "#0084c7", fontWeight: 600 }}>
							4 parameters
						</span>{" "}
						- carrier VAT ID, contact person's email, OCP, and paste the
						technical email parameters.
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: "10px",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Typography sx={{ fontSize: "22px", color: "#fff" }}>
							A ready report with{" "}
							<span style={{ color: "#0084c7", fontWeight: 600 }}>
								over 60 parameters
							</span>{" "}
							from different areas and sources in just 1 minute.
						</Typography>
						<Box sx={{ position: "relative" }}>
							<Typography
								sx={{
									fontSize: "19px",
									color: "#fff",
									"& > a:hover": { textDecoration: "underline !important" },
								}}
							>
								More at{" "}
								<a
									style={{
										color: "#0084c7",
										fontWeight: 600,
										textDecoration: "none",
									}}
									href="https://www.FASTVERI.pl"
									target="_blank"
									rel="noopener noreferrer"
								>
									www.<span style={{ color: "#fff" }}>FAST</span>VERI.pl
									<AdsClickIcon
										sx={{
											position: "absolute",
											top: "14px",
											right: "-20px",
											color: "#fff",
											animation: "bounce 2s infinite",
										}}
									/>
								</a>
							</Typography>
						</Box>
					</Box>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						marginTop: "10px",
					}}
				>
					<Typography sx={{ fontSize: "22px", color: "#fff" }}>
						Would you like to verify faster, more conveniently, and protect
						yourself more effectively against fraudsters?
					</Typography>
				</Box>
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						gap: "20px",
						marginTop: "20px",
						paddingBottom: "16px",
					}}
				>
					<Button
						variant="contained"
						onClick={onSignUp}
						disabled={isSigningUp}
						sx={{
							fontSize: "16px",
							"&:disabled": {
								background: "rgba(99, 194, 154, 0.3)",
								color: "#fff",
							},
						}}
					>
						I'M INTERESTED
					</Button>
					<Button
						variant="contained"
						color="error"
						disabled={lockCloseButton}
						onClick={() => setOpen(false)}
						sx={{
							fontSize: "16px",
							textTransform: "uppercase",
							background: "#585858",
							"&:hover": {
								background: "#474646",
							},
							"&:disabled": {
								background: "rgba(88, 88, 88, 0.3)",
								color: "#fff",
								cursor: "not-allowed",
							},
						}}
					>
						I'M NOT INTERESTED {lockCounter > 0 && `(${lockCounter})`}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
